import React from 'react';
import { Calendar } from 'lucide-react';
import Badge from '@components/common/Badge';
import { RZTable } from '@components/common/table';
import { AssessorCell } from '@components/common/table';
import { InfoCard } from '@components/common';
import { LoadingState } from '@components/common/loading';
import { formatMediumDateTime } from '@utils/dateUtils';
import { meetingStatusToVariant } from '@utils/badgeMappers';
import { getMeetingStatusLabel } from '@utils/statusUtils';
import type { ContractRelatedMeeting, TableColumn } from '@app-types';
import styles from './ContractMeetings.module.css';

interface ContractMeetingsProps {
  meetings: ContractRelatedMeeting[];
  isLoading?: boolean;
  onViewMeeting?: (meetingId: number) => void;
}

const contractMeetingColumns: TableColumn[] = [
  { key: 'scheduled_datetime', label: 'Meeting Date', defaultVisible: true, alwaysVisible: true },
  { key: 'scheduled_by', label: 'Scheduled By', defaultVisible: true },
  { key: 'assessor', label: 'Assessor', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true }
];

const ContractMeetings: React.FC<ContractMeetingsProps> = ({
  meetings,
  isLoading = false,
  onViewMeeting
}) => {
  const renderCell = (column: TableColumn, meeting: ContractRelatedMeeting) => {
    switch (column.key) {
      case 'scheduled_datetime':
        return (
          <button
            type="button"
            className={styles.meetingLink}
            onClick={() => onViewMeeting?.(meeting.id)}
          >
            {formatMediumDateTime(meeting.scheduled_datetime, 'Not scheduled')}
          </button>
        );

      case 'scheduled_by':
        return meeting.scheduled_by_name || 'N/A';

      case 'assessor':
        return <AssessorCell assessorName={meeting.assessor_name} />;

      case 'status':
        return <Badge variant={meetingStatusToVariant(meeting.status)}>{getMeetingStatusLabel(meeting.status)}</Badge>;

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <InfoCard>
        <InfoCard.Header>
          <InfoCard.Title icon={<Calendar size={18} />}>Related Meetings</InfoCard.Title>
        </InfoCard.Header>
        <InfoCard.Content>
          <LoadingState message="Loading meetings..." />
        </InfoCard.Content>
      </InfoCard>
    );
  }

  return (
    <InfoCard>
      <InfoCard.Header>
        <InfoCard.Title icon={<Calendar size={18} />}>Related Meetings ({meetings.length})</InfoCard.Title>
      </InfoCard.Header>

      <InfoCard.Content>
        <RZTable
          data={meetings}
          columns={contractMeetingColumns}
          getRowKey={(meeting) => meeting.id}
          renderCell={renderCell}
          state={{
            isLoading: false,
            isEmpty: meetings.length === 0,
            emptyTitle: 'No meetings found',
            emptyDescription: 'No meetings found for this contract.',
            emptyType: 'meetings'
          }}
        />
      </InfoCard.Content>
    </InfoCard>
  );
};

export default ContractMeetings;
